<template>
    <div class="file-attachment-card" :class="{ 'is-failed': failed }">
        <el-icon class="file-icon" :size="22">
            <component :is="iconComp" />
        </el-icon>
        <div class="file-info">
            <div class="file-name" :title="fileName">{{ fileName || 'AI 报告' }}</div>
            <div class="file-meta">
                <span class="format-tag" :class="'fmt-' + (format || '').toLowerCase()">
                    {{ (format || '').toUpperCase() || 'FILE' }}
                </span>
                <span class="file-size">{{ formatSize(fileSize) }}</span>
            </div>
            <div v-if="errorMsg" class="file-error">{{ errorMsg }}</div>
        </div>
        <el-button type="primary" size="small" :loading="downloading" @click="downloadFile">
            下载
        </el-button>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { ElMessage } from 'element-plus'
import { Document, Picture, VideoCamera } from '@element-plus/icons-vue'
import { getToken } from '@/utils/auth'

const props = defineProps({
    fileId: {
        type: String,
        required: true
    },
    fileName: {
        type: String,
        default: ''
    },
    fileSize: {
        type: Number,
        default: 0
    },
    format: {
        type: String,
        default: ''
    }
})

const downloading = ref(false)
const failed = ref(false)
const errorMsg = ref('')

// 简单按后缀选图标
const iconComp = computed(() => {
    const f = (props.format || '').toLowerCase()
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(f)) return Picture
    if (['mp4', 'mov', 'avi'].includes(f)) return VideoCamera
    return Document
})

function formatSize(bytes) {
    if (!bytes || bytes <= 0) return ''
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    if (bytes < 1024 * 1024 * 1024) return (bytes / 1024 / 1024).toFixed(2) + ' MB'
    return (bytes / 1024 / 1024 / 1024).toFixed(2) + ' GB'
}

/**
 * 下载私有文件:走 fetch 带 Authorization 头,拿 blob 后用 URL.createObjectURL 触发
 * 不能用 window.open,因为 token 不会跨页面带过去
 */
async function downloadFile() {
    if (!props.fileId || downloading.value) return
    downloading.value = true
    failed.value = false
    errorMsg.value = ''
    try {
        const baseApi = import.meta.env.VITE_APP_BASE_API || ''
        const url = baseApi + '/file/privateDownload/' + props.fileId
        const token = getToken()
        const res = await fetch(url, {
            method: 'GET',
            headers: token ? { 'Authorization': token } : {}
        })
        if (!res.ok) {
            throw new Error('HTTP ' + res.status)
        }
        // 优先从响应头拿文件名,否则用 props.fileName
        let downloadName = props.fileName || ('report_' + props.fileId)
        const disposition = res.headers.get('Content-Disposition') || ''
        const m = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i)
        if (m && m[1]) {
            try {
                downloadName = decodeURIComponent(m[1])
            } catch (e) {
                downloadName = m[1]
            }
        }
        const blob = await res.blob()
        const blobUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = downloadName
        a.style.display = 'none'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        // 释放
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000)
    } catch (e) {
        failed.value = true
        errorMsg.value = '下载失败: ' + (e.message || '未知错误')
        ElMessage.error(errorMsg.value)
    } finally {
        downloading.value = false
    }
}
</script>

<style lang="scss" scoped>
.file-attachment-card {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
    padding: 10px 12px;
    background: #fff;
    border: 1px solid #e8eaef;
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    transition: border-color 0.2s;

    &:hover {
        border-color: #1890ff;
    }

    &.is-failed {
        border-color: #f56c6c;
    }
}

.file-icon {
    color: #1890ff;
    flex-shrink: 0;
}

.file-info {
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.file-name {
    font-size: 13px;
    color: #333;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.file-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 3px;
    font-size: 11px;
    color: #999;
}

.format-tag {
    padding: 1px 6px;
    border-radius: 3px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.5px;

    &.fmt-docx {
        background: #e6f7ff;
        color: #1890ff;
        border: 1px solid #91d5ff;
    }

    &.fmt-xlsx {
        background: #f6ffed;
        color: #52c41a;
        border: 1px solid #b7eb8f;
    }

    &.fmt-pdf {
        background: #fff7e6;
        color: #fa8c16;
        border: 1px solid #ffd591;
    }
}

.file-error {
    margin-top: 4px;
    font-size: 11px;
    color: #f56c6c;
}
</style>